import JSZip from "jszip";
import { PdfTemplateType } from "../../../../pdf-templates/document-template.types";
import { base64ToBlob } from "../../../../lib/utils/utils-functions";

interface GeneratedPdf {
	orgId: string;
	clientId: string;
	clientName: string;
	pdfType: PdfTemplateType;
	base64: string;
	organizationName: string;
	settingsDiff: Record<string, any>;
}

// Map PDF template types to short abbreviations
const PDF_TYPE_ABBREVIATIONS: Record<PdfTemplateType, string> = {
	[PdfTemplateType.KOSTENVORANSCHLAG]: "KV",
	[PdfTemplateType.LIEFERSCHEIN]: "LS",
	[PdfTemplateType.RECHNUNG]: "RG",
	[PdfTemplateType.SAMMELRECHNUNG]: "SRG",
	[PdfTemplateType.RECHNUNGSDOPPEL]: "RG_DOPPEL",
	[PdfTemplateType.ZAHLUNGSERINNERUNG]: "ZAHLUNGSERINNERUNG",
	[PdfTemplateType.ERSTE_MAHNUNG]: "MAHNUNG_1",
	[PdfTemplateType.FOLGEMAHNUNG]: "MAHNUNG_2",
	[PdfTemplateType.LETZTE_MAHNUNG]: "LETZTE_MAHNUNG",
	[PdfTemplateType.BARZAHLUNGSQUITTUNG]: "BAR",
	[PdfTemplateType.KARTENZAHLUNGSQUITTUNG]: "KARTE",
	[PdfTemplateType.AKONTORECHNUNG]: "AKONTO",
	[PdfTemplateType.GUTSCHRIFT]: "GS",
	[PdfTemplateType.AUFTRAGSBLATT]: "AUFTRAGSBLATT",
	[PdfTemplateType.ETIK_AUFTRAG]: "ETIK_AUFTRAG",
};

/**
 * Sanitizes a string for use in filenames
 * Replaces unsafe characters and trims whitespace
 */
const sanitizeFilename = (name: string): string => {
	return name
		.replace(/[<>:"/\\|?*\x00-\x1F]/g, "_") // Replace unsafe characters
		.replace(/\s+/g, "_") // Replace whitespace with underscores
		.replace(/_{2,}/g, "_") // Replace multiple underscores with single
		.replace(/^_+|_+$/g, "") // Trim underscores from start/end
		.substring(0, 100); // Limit length to prevent filesystem issues
};

/**
 * Formats setting key-value pairs into abbreviated filename components
 */
const formatSettingsForFilename = (
	settingsDiff: Record<string, any>
): string => {
	if (Object.keys(settingsDiff).length === 0) {
		return "";
	}

	const abbreviations: string[] = [];

	for (const [key, value] of Object.entries(settingsDiff)) {
		let abbrev = "";

		switch (key) {
			case "vat_display_override":
				abbrev =
					value === "inclusive"
						? "mwst-inkl"
						: value === "exclusive"
							? "mwst-exkl"
							: `mwst-${value}`;
				break;
			case "patient_name_format":
				if (value === "first_last") abbrev = "pat-vn";
				else if (value === "last_first") abbrev = "pat-nv";
				else if (value === "last_only") abbrev = "pat-n";
				else abbrev = `pat-${value}`;
				break;
			case "is_inv_w_job_title":
				abbrev = value ? "titel-ja" : "titel-nein";
				break;
			case "is_inv_w_pat_data":
				abbrev = value ? "patdat-ja" : "patdat-nein";
				break;
			case "srg_sorting_option":
				if (value === "job_code") abbrev = "sort-auftr";
				else if (value === "job_title") abbrev = "sort-titel";
				else if (value === "patient_last_name") abbrev = "sort-pat";
				else if (value === "patient_code") abbrev = "sort-patnr";
				else abbrev = `sort-${value}`;
				break;
			case "is_srg_grouped_by_prac":
				abbrev = value ? "grp-ja" : "grp-nein";
				break;
			case "is_show_insurance_on_pdf":
				abbrev = value ? "vers-ja" : "vers-nein";
				break;
			default:
				// Generic fallback
				abbrev = `${key}-${String(value)}`.substring(0, 20);
		}

		abbreviations.push(abbrev);
	}

	return abbreviations.join("_");
};

/**
 * Creates a zip file containing all generated PDFs
 * Structure: OrganizationName/ClientName_TYPE_settings_date.pdf
 * Zip filename: PDFs_YYYY-MM-DD_HHMMSS.zip
 */
export const createPdfZip = async (pdfs: GeneratedPdf[]): Promise<Blob> => {
	const zip = new JSZip();

	// Get current date for filenames
	const now = new Date();
	const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD

	// Group PDFs by organization name
	const pdfsByOrg = pdfs.reduce(
		(acc, pdf) => {
			const folderName = sanitizeFilename(pdf.organizationName);
			if (!acc[folderName]) {
				acc[folderName] = [];
			}
			acc[folderName].push(pdf);
			return acc;
		},
		{} as Record<string, GeneratedPdf[]>
	);

	// Create folders and add PDFs
	for (const [folderName, orgPdfs] of Object.entries(pdfsByOrg)) {
		// Create a folder for this organization
		const folder = zip.folder(folderName);

		if (!folder) continue;

		// Track filenames to handle duplicates
		const usedFilenames = new Map<string, number>();

		// Add each PDF to the organization's folder
		for (const pdf of orgPdfs) {
			const clientName = sanitizeFilename(pdf.clientName);
			const abbreviation = PDF_TYPE_ABBREVIATIONS[pdf.pdfType];
			const settingsPart = formatSettingsForFilename(pdf.settingsDiff);

			// Build base filename
			let baseFilename = `${clientName}_${abbreviation}`;
			if (settingsPart) {
				baseFilename += `_${settingsPart}`;
			}
			baseFilename += `_${dateStr}`;

			// Handle duplicates by adding index
			let filename = `${baseFilename}.pdf`;
			if (usedFilenames.has(baseFilename)) {
				const count = usedFilenames.get(baseFilename)! + 1;
				usedFilenames.set(baseFilename, count);
				filename = `${baseFilename}_${count}.pdf`;
			} else {
				usedFilenames.set(baseFilename, 1);
			}

			// Convert base64 to blob data
			const blob = base64ToBlob(pdf.base64, "application/pdf");
			folder.file(filename, blob);
		}
	}

	// Generate the zip file
	const zipBlob = await zip.generateAsync({ type: "blob" });
	return zipBlob;
};

/**
 * Downloads the zip file with a timestamped filename
 */
export const downloadPdfZip = async (pdfs: GeneratedPdf[]) => {
	try {
		const zipBlob = await createPdfZip(pdfs);

		// Create timestamp for zip filename
		const now = new Date();
		const timestamp = now
			.toISOString()
			.replace(/T/, "_")
			.replace(/:/g, "")
			.split(".")[0]; // YYYY-MM-DD_HHMMSS

		const zipFilename = `PDFs_${timestamp}.zip`;

		// Trigger download
		const url = URL.createObjectURL(zipBlob);
		const link = document.createElement("a");
		link.href = url;
		link.download = zipFilename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	} catch (error) {
		console.error("Error creating zip file:", error);
		throw error;
	}
};
